import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { RefreshCw, TrendingUp, TrendingDown, Search, Building2, DollarSign } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts'

interface CentroCustoData {
  centro_resultado: string
  valor_total_orcado: number
  valor_contratado: number
  valor_saldo_disponivel: number
  valor_movimentado_saida: number
  valor_movimentado_entrada: number
  percentual_utilizado: number
  quantidade_tipos: number
}

export default function AnaliseCentroCustoPage() {
  const [data, setData] = useState<CentroCustoData[]>([])
  const [loading, setLoading] = useState(true)
  const [anoSelecionado, setAnoSelecionado] = useState<string>('')
  const [anos, setAnos] = useState<number[]>([])
  const [busca, setBusca] = useState('')
  const [centroSelecionado, setCentroSelecionado] = useState<string>('')

  useEffect(() => {
    fetchAnos()
  }, [])

  useEffect(() => {
    if (anoSelecionado) {
      fetchData()
    }
  }, [anoSelecionado])

  const fetchAnos = async () => {
    try {
      const { data: anosData, error } = await supabase
        .from('solis_vw_orcamento_por_tipo_centro')
        .select('ano')
        .order('ano', { ascending: false })

      if (error) throw error

      const anosUnicos = [...new Set(anosData?.map((d: any) => d.ano) || [])].sort((a, b) => b - a) as number[]
      setAnos(anosUnicos)
      if (anosUnicos.length > 0 && !anoSelecionado) {
        setAnoSelecionado(String(anosUnicos[0]))
      }
    } catch (error) {
      console.error('Erro ao buscar anos:', error)
    }
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      let query = supabase
        .from('solis_vw_orcamento_por_tipo_centro')
        .select('*')
        .eq('ano', parseInt(anoSelecionado))

      if (centroSelecionado) {
        query = query.eq('centro_resultado', centroSelecionado)
      }

      const { data: viewData, error } = await query

      if (error) throw error

      // Agrupar por centro de custo
      const agrupado = (viewData || []).reduce((acc, item) => {
        const centro = item.centro_resultado || 'Sem centro'
        if (!acc[centro]) {
          acc[centro] = {
            centro_resultado: centro,
            valor_total_orcado: 0,
            valor_contratado: 0,
            valor_saldo_disponivel: 0,
            valor_movimentado_saida: 0,
            valor_movimentado_entrada: 0,
            quantidade_tipos: 0,
          }
        }
        acc[centro].valor_total_orcado += Number(item.valor_total_orcado || 0)
        acc[centro].valor_contratado += Number(item.valor_contratado || 0)
        acc[centro].valor_saldo_disponivel += Number(item.valor_saldo_disponivel || 0)
        acc[centro].valor_movimentado_saida += Number(item.valor_movimentado_saida || 0)
        acc[centro].valor_movimentado_entrada += Number(item.valor_movimentado_entrada || 0)
        acc[centro].quantidade_tipos += 1
        return acc
      }, {} as Record<string, CentroCustoData>)

      // Calcular percentual utilizado
      const dadosComPercentual = Object.values(agrupado).map((item) => ({
        ...item,
        percentual_utilizado:
          item.valor_total_orcado > 0
            ? (item.valor_contratado / item.valor_total_orcado) * 100
            : 0,
      }))

      // Ordenar por valor orçado
      dadosComPercentual.sort((a, b) => b.valor_total_orcado - a.valor_total_orcado)

      setData(dadosComPercentual)
    } catch (error) {
      console.error('Erro ao buscar dados:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  // Filtrar por busca
  const dadosFiltrados = data.filter((item) =>
    item.centro_resultado.toLowerCase().includes(busca.toLowerCase())
  )

  // Dados para gráfico
  const graficoData = dadosFiltrados.slice(0, 10).map((item) => ({
    name: item.centro_resultado.length > 20 ? item.centro_resultado.substring(0, 20) + '...' : item.centro_resultado,
    'Orçado': item.valor_total_orcado,
    'Contratado': item.valor_contratado,
    'Saldo': item.valor_saldo_disponivel,
  }))

  // Totais
  const totalOrcado = dadosFiltrados.reduce((sum, item) => sum + item.valor_total_orcado, 0)
  const totalContratado = dadosFiltrados.reduce((sum, item) => sum + item.valor_contratado, 0)
  const totalSaldo = dadosFiltrados.reduce((sum, item) => sum + item.valor_saldo_disponivel, 0)
  const percentualGeral = totalOrcado > 0 ? (totalContratado / totalOrcado) * 100 : 0

  // Lista de centros únicos para filtro
  const centrosUnicos = [...new Set(data.map((item) => item.centro_resultado))].sort()

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Análise por Centro de Custo</h2>
          <p className="text-muted-foreground text-sm">
            Análise detalhada do orçamento e utilização por centro de custo
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={anoSelecionado} onValueChange={setAnoSelecionado}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Selecione o ano" />
            </SelectTrigger>
            <SelectContent>
              {anos.map((ano) => (
                <SelectItem key={ano} value={String(ano)}>
                  {ano}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Filtros e KPIs */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orçado</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(totalOrcado)}</div>
            <p className="text-xs text-muted-foreground">{dadosFiltrados.length} centros de custo</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Contratado</CardTitle>
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{formatCurrency(totalContratado)}</div>
            <p className="text-xs text-muted-foreground">{percentualGeral.toFixed(1)}% utilizado</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Saldo Disponível</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${totalSaldo < 0 ? 'text-red-600' : 'text-blue-600'}`}>
              {formatCurrency(totalSaldo)}
            </div>
            <p className="text-xs text-muted-foreground">
              {(100 - percentualGeral).toFixed(1)}% disponível
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Centros Analisados</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dadosFiltrados.length}</div>
            <p className="text-xs text-muted-foreground">Centros de custo ativos</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros de busca */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Buscar centro de custo</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Digite o nome do centro..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Filtrar por centro</label>
              <Select value={centroSelecionado} onValueChange={(val) => {
                setCentroSelecionado(val === '__all__' ? '' : val)
                setBusca('')
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os centros" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos os centros</SelectItem>
                  {centrosUnicos.map((centro) => (
                    <SelectItem key={centro} value={centro}>
                      {centro}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gráfico */}
      {dadosFiltrados.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Comparativo Top 10 Centros de Custo</CardTitle>
            <CardDescription>Orçado vs Contratado vs Saldo Disponível</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={graficoData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={120} />
                <YAxis />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="Orçado" fill="#0088FE" />
                <Bar dataKey="Contratado" fill="#FF8042" />
                <Bar dataKey="Saldo" fill="#00C49F" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Tabela detalhada */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhamento por Centro de Custo</CardTitle>
          <CardDescription>
            Análise completa de orçamento, contratações e saldo disponível
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : dadosFiltrados.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum centro de custo encontrado com os filtros selecionados.
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Centro de Custo</TableHead>
                    <TableHead className="text-right">Orçado</TableHead>
                    <TableHead className="text-right">Contratado</TableHead>
                    <TableHead className="text-right">% Utilizado</TableHead>
                    <TableHead className="text-right">Saldo Disponível</TableHead>
                    <TableHead className="text-right">Mov. Entrada</TableHead>
                    <TableHead className="text-right">Mov. Saída</TableHead>
                    <TableHead className="text-right">Tipos</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dadosFiltrados.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{item.centro_resultado}</TableCell>
                      <TableCell className="text-right font-bold text-green-600">
                        {formatCurrency(item.valor_total_orcado)}
                      </TableCell>
                      <TableCell className="text-right font-bold text-orange-600">
                        {formatCurrency(item.valor_contratado)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 bg-muted rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                item.percentual_utilizado > 90
                                  ? 'bg-red-500'
                                  : item.percentual_utilizado > 70
                                  ? 'bg-yellow-500'
                                  : 'bg-green-500'
                              }`}
                              style={{ width: `${Math.min(item.percentual_utilizado, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium">
                            {item.percentual_utilizado.toFixed(1)}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell
                        className={`text-right font-bold ${
                          item.valor_saldo_disponivel < 0 ? 'text-red-600' : 'text-blue-600'
                        }`}
                      >
                        {formatCurrency(item.valor_saldo_disponivel)}
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        {formatCurrency(item.valor_movimentado_entrada)}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        {formatCurrency(item.valor_movimentado_saida)}
                      </TableCell>
                      <TableCell className="text-right">{item.quantidade_tipos}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

